import { useCallback, useEffect, useRef, useState } from "react";

import useTranslation from "$src/core/hooks/use-translation";
import type { TranslationKey } from "$src/core/models/translation";
import Alerter from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";
import speechRecognition from "$src/core/services/speech-recognition";
import type { SpeechRecognitionErrorCode } from "$src/core/services/speech-recognition/types";

type Params = {
  onTranscript: (text: string) => void;
};

const getErrorMessageKey = (
  code: SpeechRecognitionErrorCode,
): TranslationKey => {
  switch (code) {
    case "permission_denied":
      return "SEARCH/VOICE/PERMISSION_DENIED";
    case "permission_restricted":
      return "SEARCH/VOICE/PERMISSION_RESTRICTED";
    case "mic_unavailable":
      return "SEARCH/VOICE/MIC_UNAVAILABLE";
    case "no_speech":
      return "SEARCH/VOICE/NO_SPEECH";
    case "recognition_failed":
    default:
      return "SEARCH/VOICE/RECOGNITION_FAILED";
  }
};

const useVoiceSearch = ({ onTranscript }: Params) => {
  const { t } = useTranslation();
  const [isSupported] = useState(() => speechRecognition.isSupported());
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const isListeningRef = useRef(false);
  const isUserStoppingRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);

  /* Keep the transcript callback ref in sync with the latest prop. */
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  /* Subscribe to speech recognition events for the hook lifetime. */
  useEffect(
    () =>
      speechRecognition.subscribe({
        onStart: () => {
          isListeningRef.current = true;
          isUserStoppingRef.current = false;
          setIsListening(true);
          setAudioLevel(0);
        },
        onEnd: () => {
          isListeningRef.current = false;
          isUserStoppingRef.current = false;
          setIsListening(false);
          setAudioLevel(0);
        },
        onResult: (transcript, isFinal) => {
          if (isFinal && transcript.trim()) {
            onTranscriptRef.current(transcript.trim());
          }
        },
        onVolumeChange: (level) => {
          setAudioLevel(level);
        },
        onError: (code) => {
          setAudioLevel(0);

          if (code === "cancelled" || isUserStoppingRef.current) {
            return;
          }

          Alerter.error(t(getErrorMessageKey(code)));
        },
      }),
    [t],
  );

  const runToggleListening = useCallback(async () => {
    if (isListeningRef.current) {
      isUserStoppingRef.current = true;
      speechRecognition.stop();
      return;
    }

    const { granted, restricted } =
      await speechRecognition.requestPermissions();

    if (restricted) {
      Alerter.error(t("SEARCH/VOICE/PERMISSION_RESTRICTED"));
      return;
    }

    if (!granted) {
      Alerter.error(t("SEARCH/VOICE/PERMISSION_DENIED"));
      return;
    }

    if (!speechRecognition.isAvailable()) {
      Alerter.error(t("SEARCH/VOICE/MIC_UNAVAILABLE"));
      return;
    }

    speechRecognition.start();
  }, [t]);

  const toggleListening = useCallback(() => {
    runToggleListening().catch((error) => {
      Logging.error("Voice search toggle failed", { error });
    });
  }, [runToggleListening]);

  return {
    isSupported,
    isListening,
    audioLevel,
    toggleListening,
  };
};

export default useVoiceSearch;
